/* eslint no-param-reassign: 0 */
import Hls from 'hls.js';
import { useCallback, useContext, useEffect } from 'react';

import {
  setMediaApi,
  updateAudioTrackIndex,
  updateAudioTracks,
  updateCanPlay,
  updateCurrentTime,
  updateDuration,
  updateIsMuted,
  updateIsPaused,
  updateSeeking,
  updateSelectedSubtitle,
  updateState,
  updateSubtitleEnabled,
  updateTextTracks,
  updateVolume,
} from '../MediaElementProvider/actions';
import {
  MediaElementContext,
  MediaElementDispatchContext,
} from '../MediaElementProvider/media-element-provider';
import { MediaElement } from '../MediaElementProvider/types';

export const getSubtitle = (textTracks: TextTrackList) => {
  for (let i = 0; i < textTracks.length; i += 1) {
    if (textTracks[i].mode === 'showing') {
      return textTracks[i];
    }
  }
  return null;
};

export const useMediaElementApi = (
  mediaEl: HTMLMediaElement | null,
  hls: Hls | null,
): MediaElement => {
  const mediaElementContext = useContext(MediaElementContext);
  const mediaStateDispatch = useContext(MediaElementDispatchContext);

  const checkSubtitleState = useCallback(
    (textTracks: TextTrackList) => {
      if (mediaStateDispatch) {
        const subtitle = getSubtitle(textTracks);
        mediaStateDispatch(updateSelectedSubtitle(subtitle));
        mediaStateDispatch(updateSubtitleEnabled(!!subtitle));
      }
    },
    [mediaStateDispatch],
  );

  const disableSubtitles = useCallback(() => {
    if (mediaEl) {
      const { textTracks } = mediaEl;
      const len = textTracks.length;
      let i = 0;
      for (i; i < len; i += 1) {
        textTracks[i].mode = 'disabled';
      }
    }
  }, [mediaEl]);

  const enableSubtitles = useCallback(
    (language: string) => {
      if (mediaEl) {
        const { textTracks } = mediaEl;
        const len = textTracks.length;
        let i = 0;
        for (i; i < len; i += 1) {
          const textTrack = textTracks[i];
          if (textTrack.language === language) {
            textTrack.mode = 'showing';
          } else {
            textTrack.mode = 'disabled';
          }
        }
      }
    },
    [mediaEl],
  );

  const play = useCallback(() => {
    mediaEl?.play();
  }, [mediaEl]);

  const pause = useCallback(() => {
    mediaEl?.pause();
  }, [mediaEl]);

  const seekTo = useCallback(
    (time: number) => {
      if (mediaEl) {
        mediaEl.currentTime = time;
        if (mediaStateDispatch) {
          mediaStateDispatch(updateCurrentTime(mediaEl.currentTime));
        }
      }
    },
    [mediaEl, mediaStateDispatch],
  );

  const setVolume = useCallback(
    (volume: number) => {
      if (mediaEl) {
        mediaEl.volume = volume;
      }
    },
    [mediaEl],
  );

  const mute = useCallback(() => {
    if (mediaEl) {
      mediaEl.muted = true;
    }
  }, [mediaEl]);

  const unMute = useCallback(() => {
    if (mediaEl) {
      mediaEl.muted = false;
    }
  }, [mediaEl]);

  const handlePlaybackStateEvents = useCallback(
    (event: Event) => {
      if (mediaEl && mediaStateDispatch) {
        if (event.type === 'canplay') {
          mediaStateDispatch(updateCanPlay(true));
        }
      }
    },
    [mediaEl, mediaStateDispatch],
  );

  const handleDurationChange = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        const { duration } = mediaEl;
        mediaStateDispatch(updateDuration(duration));
      }
    }
  }, [mediaEl, mediaStateDispatch]);

  const handleTimeUpdate = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        const { currentTime, textTracks } = mediaEl;
        checkSubtitleState(textTracks);
        mediaStateDispatch(updateIsPaused(false));
        mediaStateDispatch(updateCurrentTime(currentTime));
      }
    }
  }, [checkSubtitleState, mediaEl, mediaStateDispatch]);

  const handlePlayerPause = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        mediaStateDispatch(updateIsPaused(true));
      }
    }
  }, [mediaEl, mediaStateDispatch]);

  const handlePlayerPlay = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        mediaStateDispatch(updateIsPaused(false));
      }
    }
  }, [mediaEl, mediaStateDispatch]);

  const handleVolumeChange = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        mediaStateDispatch(updateVolume(mediaEl.volume));
        mediaStateDispatch(updateIsMuted(mediaEl.muted));
      }
    }
  }, [mediaEl, mediaStateDispatch]);

  const handleSeeking = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        mediaStateDispatch(updateSeeking(true));
        mediaStateDispatch(updateIsPaused(true));
      }
    }
  }, [mediaEl, mediaStateDispatch]);

  const handleSeeked = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        mediaStateDispatch(updateSeeking(false));
      }
    }
  }, [mediaEl, mediaStateDispatch]);

  const handleUpdateTextTrack = useCallback(() => {
    if (mediaEl) {
      if (mediaStateDispatch) {
        const { textTracks } = mediaEl;
        mediaStateDispatch(updateTextTracks(textTracks));
        checkSubtitleState(textTracks);
      }
    }
  }, [checkSubtitleState, mediaEl, mediaStateDispatch]);

  const switchAudioTrack = useCallback(
    (trackIndex: number) => {
      if (hls && hls.audioTracks && trackIndex < hls.audioTracks.length && mediaStateDispatch) {
        hls.audioTrack = trackIndex;
        mediaStateDispatch(updateAudioTrackIndex(trackIndex));
      } else {
        console.warn(`Audio track index ${trackIndex} is out of bounds.`);
      }
    },
    [hls, mediaStateDispatch],
  );

  useEffect(() => {
    if (mediaEl) {
      mediaEl.addEventListener('canplay', handlePlaybackStateEvents);
      mediaEl.addEventListener('stalled', handlePlaybackStateEvents);
      mediaEl.addEventListener('suspend', handlePlaybackStateEvents);
      mediaEl.addEventListener('waiting', handlePlaybackStateEvents);
      mediaEl.addEventListener('durationchange', handleDurationChange);
      mediaEl.addEventListener('timeupdate', handleTimeUpdate);
      mediaEl.addEventListener('pause', handlePlayerPause);
      mediaEl.addEventListener('play', handlePlayerPlay);
      mediaEl.addEventListener('volumechange', handleVolumeChange);
      mediaEl.addEventListener('seeking', handleSeeking);
      mediaEl.addEventListener('seeked', handleSeeked);
      mediaEl.textTracks?.addEventListener?.('addtrack', handleUpdateTextTrack);
      mediaEl.textTracks?.addEventListener?.('removetrack', handleUpdateTextTrack);
    }

    if (hls && mediaStateDispatch) {
      hls?.on(Hls.Events.AUDIO_TRACKS_UPDATED, (_, data) => {
        mediaStateDispatch(updateAudioTracks(data.audioTracks));
      });
      hls?.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_, data) => {
        const trackIndex = hls?.audioTracks.findIndex((track) => track.id === data.id);
        mediaStateDispatch(updateAudioTrackIndex(trackIndex));
      });
    }

    if (mediaEl && mediaStateDispatch) {
      const subtitle = getSubtitle(mediaEl.textTracks);
      mediaStateDispatch(
        updateState({
          audioTrackIndex: hls?.audioTrack ?? 0,
          audioTracks: hls?.audioTracks ?? [],
          canPlay: false,
          currentTime: mediaEl.currentTime,
          duration: mediaEl.duration,
          isMuted: mediaEl.muted,
          isPaused: mediaEl.paused,
          isPlaying: false,
          seeking: mediaEl.seeking,
          selectedSubtitle: subtitle,
          subtitleEnabled: !!subtitle,
          textTracks: mediaEl.textTracks,
          volume: mediaEl.volume,
        }),
      );
      mediaStateDispatch(
        setMediaApi({
          disableSubtitles,
          enableSubtitles,
          mute,
          pause,
          play,
          seekTo,
          setVolume,
          switchAudioTrack,
          unMute,
        }),
      );
    }
    return () => {
      if (mediaEl) {
        mediaEl.removeEventListener('durationchange', handleDurationChange);
        mediaEl.removeEventListener('timeupdate', handleTimeUpdate);
        mediaEl.removeEventListener('pause', handlePlayerPause);
        mediaEl.removeEventListener('play', handlePlayerPlay);
        mediaEl.removeEventListener('seeking', handleSeeking);
        mediaEl.removeEventListener('seeked', handleSeeked);
        mediaEl.textTracks?.removeEventListener?.('addtrack', handleUpdateTextTrack);
        mediaEl.textTracks?.removeEventListener?.('removetrack', handleUpdateTextTrack);
      }
    };
  }, [
    disableSubtitles,
    enableSubtitles,
    handleDurationChange,
    handlePlaybackStateEvents,
    handlePlayerPause,
    handlePlayerPlay,
    handleSeeked,
    handleSeeking,
    handleTimeUpdate,
    handleUpdateTextTrack,
    handleVolumeChange,
    mute,
    pause,
    play,
    seekTo,
    setVolume,
    unMute,
    mediaEl,
    mediaStateDispatch,
    switchAudioTrack,
    hls,
  ]);

  return {
    mediaApi: mediaElementContext.mediaApi,
    mediaState: mediaElementContext.mediaState,
  };
};
